import { Box, useMantineTheme } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { useContext } from 'react';
import SingleCustomerContext from '../Customer-Context/SingleCustomerContext';
import { TitleCellMain } from './TabelCells/TitleCellMain';
import { ExpandedTable } from './ExpandedTable';
import { useCustomerTableData } from './useCustomerTableData';
import { CenterCell } from './TabelCells/CenterCell';
import { CountCell } from './TabelCells/CountCell';
import { LIGHT_PURPULE_BACKGROUND } from '../../../constants/colors';
import { HeaderCell } from './TabelCells/HeaderCell';

// Define interfaces for our data
interface Component {
  _id: string;
  name: string;
  [key: string]: any;
}

interface CustomerTableProps {
  isModalOpen: boolean;
}

export function CustomerTable({ isModalOpen }: CustomerTableProps) {
  const theme = useMantineTheme();
  
  const { 
    threeSixtyColumns,
    setChosenIdea,
    ideasLoading,
    setCardOpened,
    viewType
  
  } = useContext(SingleCustomerContext);

  const { mainItems, expandedItemsIds, groupBy, setExpandedItemsIds, sortedIdeas } = useCustomerTableData();

  const middleColumn = (threeSixtyColumns.length / 2) -1
  
  // Create columns array with accessor as key and label property from ideaStatuses
  const statusesColumns = (threeSixtyColumns || []).map((status, index) => {
    if (index === middleColumn) {
      return {
        accessor: status.label,
        title: status.label,
        width: 136.63,
        textAlign: 'center',
        statuses: status.statuses,
        render: (record: Component) => {
          const isExpanded = expandedItemsIds.includes(record._id);
          if (!isExpanded) {
            return (
              <CountCell 
                record={record}
                groupBy={groupBy}
                statuses={status.statuses}
                sortedIdeas={sortedIdeas}
              />
            );
          }
          
          return <CenterCell record={record} isModalOpen={isModalOpen} />;
        }
      };
    }
    return {
      accessor: status.label,
      title: status.label,
      width: 136.63,
      textAlign: 'center',
      statuses: status.statuses,
      render: (record: Component) => {
        const isExpanded = expandedItemsIds.includes(record._id);
        if (!isExpanded) {
          return (
            <CountCell 
              record={record}
              groupBy={groupBy}
              statuses={status.statuses}
              sortedIdeas={sortedIdeas}
            />
          );
        }
        
        return null; // No content when collapsed for other columns
      }
    };
  });

  return (
    <Box sx={{
      width: '100%',
      overflow: 'auto',
      maxWidth: 'calc(100vw - 32px)',
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        '& .mantine-DataTable-root': {
          minWidth: '800px',
        }
      }
    }}>
      <DataTable
        fetching={ideasLoading} 
        key="main-table"
        withBorder
        highlightOnHover
        height="calc(100vh - 308px)"
        sx={{
          borderRadius: "2px",
          overflow: "hidden",
          width: '100%',
          "tbody tr": {
            height: "48px",
            backgroundColor: LIGHT_PURPULE_BACKGROUND,
            verticalAlign: "middle",
            textAlign: "center",
            position: "relative",
            zIndex: 1
          },
          "tbody tr:not([class])": {
            height: "0",
          },
          "td": {
            borderRight: "none",
            verticalAlign: "middle",
            padding: "8px 16px",
            [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
              padding: "8px 8px",
            }
          },
          "th": {
            verticalAlign: "middle",
            height: "48px",
            textAlign: "center",
            backgroundColor: LIGHT_PURPULE_BACKGROUND,
            borderLeft: "1px solid #dee2e6",
            position: "relative",
            zIndex: 2,
            "& > div": {
              justifyContent: "center",
              textAlign: "center",
              width: "90%"
            },
            [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
              padding: "8px 8px",
            }
          },
          "td:first-of-type": {
            borderRight: "1px solid #dee2e6",
          }
        }}
        columns={[
          {
            accessor: 'name',
            title: <HeaderCell />,
            width: 251,
            noWrap: true,
            render: (record: Component) => (
              <TitleCellMain 
                record={record} 
                isExpanded={expandedItemsIds.includes(record._id)}
              />
            ),
          },
          ...statusesColumns
        ]}
        records={mainItems}
        rowExpansion={{
          allowMultiple: true,
          expanded: { recordIds: expandedItemsIds, onRecordIdsChange: setExpandedItemsIds },
          content: (component) => (
            <ExpandedTable
              ideas={sortedIdeas.filter(idea => idea[groupBy] === component.record._id)}
              statusesColumns={statusesColumns}
              onRowClick={(record) => {
                setChosenIdea(record)
                setCardOpened(true)
              }}
            />
          ),
        }}
      />
    </Box>
  );
} 